import React, { useMemo, useState } from "react";
import PageShell from "../dashboard/PageShell";
import { PlusCircle, MinusCircle } from "lucide-react";

type AllocationRow = {
  fundSource: string; // select: - Chọn nguồn kinh phí -
  disbursementYear: string; // - Chọn năm cấp -
  amount: string; // number text
};

const defaultAllocationRow = (): AllocationRow => ({
  fundSource: "",
  disbursementYear: "",
  amount: "0",
});

const SignImplementationContract: React.FC = () => {
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      // expected: topics/approval/detail/:id/contract/sign → id at index length-3
      return parts[parts.length - 3] || "";
    } catch {
      return "";
    }
  }, []);

  const [form, setForm] = useState({
    contractNumber: "",
    contractSignedDate: "",
    approvedBudget: "",
    startMonth: "",
    startYear: "",
    endMonth: "",
    endYear: "",
    allocations: [defaultAllocationRow()],
  });

  const months = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
  const years = Array.from({ length: 31 }, (_, i) => `${2015 + i}`);

  const change = (field: string, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));
  const changeAlloc = (
    index: number,
    field: keyof AllocationRow,
    value: string
  ) =>
    setForm((p) => {
      const next = [...p.allocations];
      next[index] = { ...next[index], [field]: value } as AllocationRow;
      return { ...p, allocations: next };
    });
  const addAllocationRow = () =>
    setForm((p) =>
      p.allocations.length >= 5
        ? p
        : { ...p, allocations: [...p.allocations, defaultAllocationRow()] }
    );
  const removeAllocationRow = (index: number) =>
    setForm((p) => {
      if (p.allocations.length <= 1) return p;
      const next = p.allocations.filter((_, i) => i !== index);
      return { ...p, allocations: next };
    });

  return (
    <PageShell title="Ký hợp đồng thực hiện">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Ký hợp đồng thực hiện
          </h1>
        </div>

        {/* Topic header */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-800 font-medium">Tên đề tài:</div>
              <div className="text-gray-700">
                Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI
              </div>
            </div>
            <div>
              <div className="text-gray-800 font-medium">Chủ nhiệm:</div>
              <div className="text-gray-700">
                Thượng tá, VS. ThS Lê Anh Tuấn
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg p-4 space-y-6">
          {/* Row 1: contract number, signed date, approved budget */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Số hợp đồng/ Quyết định{" "}
                <span className="text-red-500">(*)</span>
              </label>
              <input
                type="text"
                value={form.contractNumber}
                onChange={(e) => change("contractNumber", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập số hợp đồng/ quyết định"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ngày ký hợp đồng/Quyết định{" "}
                <span className="text-red-500">(*)</span>
              </label>
              <input
                type="date"
                value={form.contractSignedDate}
                onChange={(e) => change("contractSignedDate", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kinh phí phê duyệt <span className="text-red-500">(*)</span>
              </label>
              <input
                type="text"
                value={form.approvedBudget}
                onChange={(e) => change("approvedBudget", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập số tiền"
              />
            </div>
          </div>

          {/* Row 2: Duration start and end */}
          <div>
            <div className="text-sm font-medium text-gray-800 mb-2">
              Thời gian thực hiện đề tài
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tháng <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={form.startMonth}
                  onChange={(e) => change("startMonth", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Năm <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={form.startYear}
                  onChange={(e) => change("startYear", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tháng <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={form.endMonth}
                  onChange={(e) => change("endMonth", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Năm <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={form.endYear}
                  onChange={(e) => change("endYear", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Row 3: Allocation simple rows */}
          <div className="w-full p-0">
            <div className="text-sm font-medium text-gray-800 mb-2">
              Kinh phí phân bổ:
            </div>
            <div className="space-y-3">
              {form.allocations.map((row, index) => {
                const canAddMore = form.allocations.length < 5;
                return (
                  <div
                    key={index}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center py-1 border-b border-gray-100 last:border-b-0"
                  >
                    <select
                      value={row.fundSource}
                      onChange={(e) =>
                        changeAlloc(index, "fundSource", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md lg:col-span-6"
                    >
                      <option value="">- Chọn nguồn kinh phí -</option>
                      <option value="nsnn">Ngân sách nhà nước</option>
                      <option value="tucho">Tự chủ</option>
                      <option value="hop-tac">Hợp tác</option>
                    </select>
                    <select
                      value={row.disbursementYear}
                      onChange={(e) =>
                        changeAlloc(index, "disbursementYear", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md lg:col-span-2"
                    >
                      <option value="">- Chọn năm cấp -</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <div className="w-full lg:col-span-3 flex items-center gap-2">
                      <input
                        type="text"
                        value={row.amount}
                        onChange={(e) =>
                          changeAlloc(index, "amount", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        VNĐ
                      </span>
                    </div>
                    <div className="flex gap-1 lg:col-span-1 justify-center">
                      {form.allocations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAllocationRow(index)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-rose-600 hover:bg-rose-100 transition"
                          title="Xóa dòng"
                          aria-label="Xóa dòng kinh phí"
                        >
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      )}
                      {canAddMore && (
                        <button
                          type="button"
                          onClick={addAllocationRow}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-emerald-600 hover:bg-emerald-100 transition"
                          title="Thêm dòng"
                          aria-label="Thêm dòng kinh phí"
                        >
                          <PlusCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => window.history.back()}
          >
            Hủy
          </button>
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium"
            onClick={() => {
              console.log("Save contract sign", form);
              const projectId = id || window.location.pathname.split("/")[4];
              window.location.href = `/topics/approval/detail/${projectId}`;
            }}
          >
            Lưu dữ liệu
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default SignImplementationContract;
